import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";

/**
 * Weekly-growth celebration.
 *
 * Fires a subtle gold-particle burst whenever the client sees a NEW weekly
 * snapshot that shows any positive movement on followers, engagement, or reels.
 * De-duped per snapshot via localStorage so it fires exactly once per update.
 *
 * Props:
 *   - clientId: string   (namespacing key so multiple clients on one browser
 *                         don't stomp each other's "last seen")
 *   - metrics: {
 *       recorded_at: iso string of the latest snapshot,
 *       deltas: { followers, engagement_rate, reel_views, avg_likes, avg_comments }
 *     }
 */
export default function GrowthCelebration({ clientId, metrics }) {
    const fired = useRef(false);

    useEffect(() => {
        if (fired.current) return;
        if (!clientId || !metrics || !metrics.recorded_at) return;

        const deltas = metrics.deltas || {};
        // Any positive movement counts as "growth this week".
        const grew = ["followers", "engagement_rate", "reel_views", "avg_likes", "avg_comments"]
            .some((k) => typeof deltas[k] === "number" && deltas[k] > 0);
        if (!grew) return;

        const key = `aestherium_last_celebrated_${clientId}`;
        const already = window.localStorage.getItem(key);
        if (already === metrics.recorded_at) return;
        window.localStorage.setItem(key, metrics.recorded_at);
        fired.current = true;

        // Gold + champagne palette to match the brand
        const colors = ["#d4af37", "#f3e5ab", "#ffe08a", "#fdfbf7"];

        // Staggered three-burst spray from the bottom center for a cinematic feel
        const bursts = [
            { delay: 300, particleCount: 60, spread: 70, startVelocity: 45, origin: { x: 0.5, y: 0.85 } },
            { delay: 700, particleCount: 40, spread: 100, startVelocity: 35, origin: { x: 0.25, y: 0.85 } },
            { delay: 1000, particleCount: 40, spread: 100, startVelocity: 35, origin: { x: 0.75, y: 0.85 } },
        ];

        const timers = bursts.map((b) =>
            setTimeout(() => {
                confetti({
                    particleCount: b.particleCount,
                    spread: b.spread,
                    startVelocity: b.startVelocity,
                    origin: b.origin,
                    colors,
                    scalar: 1.1,
                    gravity: 0.9,
                    ticks: 220,
                    disableForReducedMotion: true,
                    zIndex: 60,
                });
            }, b.delay),
        );

        return () => timers.forEach(clearTimeout);
    }, [clientId, metrics]);

    return null;
}
